/**
 * Sincronizare de sesiune ÎNTRE taburi (hardening 2026-06-11).
 *
 * Problema reală care a motivat-o: un tab vechi, rămas deschis pe ALT cont,
 * și-a rotit refresh-token-ul la un minut după un login nou și a rescris
 * cookie-urile de sesiune (domeniu partajat) → UI hibrid: header-ul arăta
 * contul nou (store-ul populat la login), datele veneau de la contul vechi
 * (cookie-ul curent).
 *
 * Două plase, ambele anunțate prin același canal:
 *  1. cross-tab: login/logout/switch-tenant publică identitatea nouă; taburile
 *     cu altă identitate se reîncarcă (preiau sesiunea curentă din cookie);
 *  2. same-tab: după fiecare POST /auth/refresh, clientul API compară
 *     identitatea din răspuns cu cea afișată (callback înregistrat de
 *     auth-store) — mismatch → reload. Astfel starea „header un cont, date
 *     alt cont" devine imposibilă indiferent de ordinea race-urilor.
 *
 * Pattern BroadcastChannel + SSR guard copiat din use-draft-backup.ts.
 */

export interface SessionIdentity {
  /** null = sesiune încheiată (logout). */
  userId: string | null;
  tenantId: string | null;
}

const CHANNEL_NAME = 'bc:sf-session';

/**
 * Id-ul acestui tab (document). BroadcastChannel livrează mesajul tuturor canalelor cu același
 * nume, inclusiv celor din ACELAȘI tab (doar obiectul care a trimis e sărit), deci fiecare tab
 * își ignoră singur propriile anunțuri.
 */
const TAB_ID = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

/**
 * Mismatch = taburile NU mai vorbesc despre aceeași sesiune.
 * Reguli (pure, testabile fără DOM):
 *  - ambele neautentificate → nu;
 *  - una autentificată, cealaltă nu → da;
 *  - useri diferiți SAU același user pe tenant diferit (switch firmă) → da.
 */
export function identityMismatch(current: SessionIdentity, incoming: SessionIdentity): boolean {
  if (!current.userId && !incoming.userId) return false;
  if (!current.userId || !incoming.userId) return true;
  if (current.userId !== incoming.userId) return true;
  // tenantId null pe oricare parte = necunoscut încă (ex. profil în curs de
  // încărcare) — nu forțăm reload pe informație incompletă.
  if (current.tenantId && incoming.tenantId && current.tenantId !== incoming.tenantId) return true;
  return false;
}

function getChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null; // SSR / browsere vechi
  return new BroadcastChannel(CHANNEL_NAME);
}

/** Publică identitatea sesiunii curente către TOATE celelalte taburi. */
export function announceSession(identity: SessionIdentity): void {
  try {
    const ch = getChannel();
    if (!ch) return;
    ch.postMessage({ userId: identity.userId, tenantId: identity.tenantId, from: TAB_ID });
    ch.close();
  } catch { /* canal indisponibil — plasa cross-tab e best-effort */ }
}

/** Ascultă anunțurile celorlalte taburi (ale sale le ignoră). Returnează cleanup. */
export function listenSession(onIdentity: (identity: SessionIdentity) => void): () => void {
  const ch = getChannel();
  if (!ch) return () => {};
  const handler = (e: MessageEvent) => {
    const d = e.data as (SessionIdentity & { from?: string }) | undefined;
    if (!d || typeof d !== 'object' || !('userId' in d) || d.from === TAB_ID) return;
    onIdentity({ userId: d.userId, tenantId: d.tenantId });
  };
  ch.addEventListener('message', handler);
  return () => {
    ch.removeEventListener('message', handler);
    ch.close();
  };
}
